<template>
  <div class="mt-1 px-3 pt-2 pb-4 filters-container">
    <b-row>
      <b-col cols="9">
        <label class="fieldLabel" for="name" v-t="'search.label.productIssuerName'"></label>
        <custom-search-bar
          :placeholder="$t('search.label.productIssuerName')"
          @on-search="onSearch"
          :textValue="searchBar"
          :debounce="800"
        ></custom-search-bar>
      </b-col>
      <b-col cols="3">
        <div class="d-flex search-actions">
          <div class="ml-1">
            <b-button @click="onResetFilters" variant="link" :disabled="!hasFiltersApplied" class="reset-button">
              {{ $t('search.label.resetFilters') }}
              <i class="fa fa-trash small ml-1 icon-size-10"></i>
              <span class="ml-2">
                <b-badge variant="secondary">{{ countFiltersSelected }}</b-badge>
              </span>
            </b-button>
          </div>
          <div class="ml-3">
            <b-button @click="onClickButton" size="sm" variant="primary" class="ml-2" data-test="search-filter-btn">
              {{ $tc('search.label.filtersList') }}
              <span><i class="fas fa-filter ml-1 icon-size-10"></i></span>
              <span :class="[isFilterSectionOpen ? 'openingRotation' : 'closingRotation']">
                <i class="py-0 ml-1 fa fa-chevron-down icon-size-12"></i>
              </span>
            </b-button>
          </div>
        </div>
      </b-col>
    </b-row>
    <b-row v-if="isFilterSectionOpen" class="mt-3">
      <b-col cols="4" class="ml-2" v-if="!isProfileEditing">
        <label class="fieldLabel" for="name" v-t="'search.label.score'"></label>
        <custom-slider :value.sync="_sliderScore" :min="0" :max="1" :interval="0.01" :tooltip="'none'"></custom-slider>
        <div class="text-right">({{ scoringRange }})</div>
      </b-col>
      <b-col cols="3">
        <label class="fieldLabel" for="name" v-t="'search.label.issuer'"></label>
        <custom-multi-select
          :options="issuerOptions"
          :default-text="$t('common.allMale')"
          v-model="_issuersFilter"
          show-all
          use-ticks
          :select-all-text="$t('common.selectAllMale')"
          show-clear-selection
          multiple
        >
        </custom-multi-select>
      </b-col>
      <b-col cols="3" v-if="!isProfileEditing">
        <label class="fieldLabel" for="name" v-t="'search.label.scoringProfiles'"></label>
        <custom-multi-select
          :options="filterSelectValues.efficiencyTestsOptions"
          :default-text="$t('common.allMale')"
          v-model="_compatibilityFilter"
          show-all
          use-ticks
          :select-all-text="$t('common.selectAllMale')"
          show-clear-selection
          multiple
        >
        </custom-multi-select>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import CustomMultiSelect from '../Lib/CustomMultiSelect.vue'
import CustomSearchBar from './CustomSearchBar.vue'
import CustomSlider from '../Lib/CustomSlider.vue'
import { mathRoundPercentage, valueToLabel } from '../../utils/formatter'

export default {
  components: {
    CustomMultiSelect,
    CustomSearchBar,
    CustomSlider,
  },
  props: {
    issuerOptions: Object,
    hasFiltersApplied: Boolean,
    countFiltersSelected: Number,
    sliderScoreF: Number,
    sliderScoreT: Number,
    issuersFilter: Array,
    compatibilityFilter: Array,
    searchBar: String,
    isProfileEditing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isFilterSectionOpen: false,
    }
  },
  methods: {
    onClickButton() {
      this.isFilterSectionOpen = !this.isFilterSectionOpen
    },
    onResetFilters() {
      this.$emit('reset-coverage-filters')
    },
    onSearch(newValue) {
      this.$emit('update:searchBar', newValue)
    },
  },
  computed: {
    _compatibilityFilter: {
      get() {
        return this.compatibilityFilter
      },
      set(newValue) {
        const filters = {
          scoreF: this.sliderScoreF,
          scoreT: this.sliderScoreT,
          issuers: this.issuersFilter,
          compatibility: newValue,
        }
        this.$emit('on-apply-filters', filters)
      },
    },
    _issuersFilter: {
      get() {
        return this.issuersFilter
      },
      set(newValue) {
        const filters = {
          scoreF: this.sliderScoreF,
          scoreT: this.sliderScoreT,
          issuers: newValue,
          compatibility: this.compatibilityFilter,
        }
        this.$emit('on-apply-filters', filters)
      },
    },
    _sliderScore: {
      get() {
        return [this.sliderScoreF || 0, this.sliderScoreT || 1]
      },
      set(newValue) {
        const filters = {
          scoreF: newValue[0],
          scoreT: newValue[1],
          issuers: this.issuersFilter,
          compatibility: this.compatibilityFilter,
        }
        this.$emit('on-apply-filters', filters)
      },
    },
    scoringRange() {
      return valueToLabel(
        this.sliderScoreF === 0 ? this.$t('search.label.scoringRangeND') : mathRoundPercentage(this.sliderScoreF),
        mathRoundPercentage(this.sliderScoreT)
      )
    },
  },
}
</script>

<style lang="scss">
.reset-button {
  font-size: 0.8rem;
}

.icon-size-10 {
  font-size: 10px;
}

.icon-size-12 {
  font-size: 12px;
}

.filters-container {
  border: 1pt solid var(--formOutlineColor) !important;
  border-radius: 0.2rem;
}

.search-actions {
  padding-top: 1.6rem;
}

.openingRotation svg {
  transition: transform 0.5s;
  -webkit-transform: rotate(180deg);
  -moz-transform: rotate(180deg);
  -ms-transform: rotate(180deg);
  -o-transform: rotate(180deg);
  transform: rotate(180deg);
}
.closingRotation svg {
  transition: transform 0.5s;
  -webkit-transform: rotate(0deg);
  -moz-transform: rotate(0deg);
  -ms-transform: rotate(0deg);
  -o-transform: rotate(0deg);
  transform: rotate(0deg);
}
</style>
